import { create, useStore } from 'zustand'
import { persist } from 'zustand/middleware'

import { isValidToken } from 'src/auth/context/jwt/utils';

export const store = create()(
  persist(
    (set, get) => ({
      isReady: false,
      // List of active sessions { sessionId, user } (Persisted)
      sessions: [],
      // The currently active session ID string (Persisted)
      activeSessionId: null,

      // Access token for the active session (Persisted)
      accessToken: null,

      // Helper to check if we have any active session
      logged: false,

      login: (sessionId, user, accessToken) => set((state) => {
        // Check if session already exists
        const existingIndex = state.sessions.findIndex(s => s.sessionId === sessionId);

        let newSessions;
        if (existingIndex >= 0) {
          // Update existing
          newSessions = [...state.sessions];
          newSessions[existingIndex] = { sessionId, user };
        } else {
          // Add new
          newSessions = [...state.sessions, { sessionId, user }];
        }

        return { sessions: newSessions, activeSessionId: sessionId, accessToken: accessToken || null }
      }),

      updateUser: (sessionId, user) => set((state) => {
        const existingIndex = state.sessions.findIndex(s => s.sessionId === sessionId);
        if (existingIndex < 0) {
          return {};
        }

        const newSessions = [...state.sessions];
        newSessions[existingIndex] = { sessionId, user };

        return { sessions: newSessions };
      }),

      switchSession: (sessionId) => set({ activeSessionId: sessionId, accessToken: null }),

      setAccessToken: (token) => set({ accessToken: token }),

      setReady: () => set({ isReady: true }),

      logout: () => set((state) => {
        const newSessions = state.sessions.filter(s => s.sessionId !== state.activeSessionId);

        // Remove token for logged out session
        // If we have other sessions, switch to the last one, otherwise null
        const newActiveId = newSessions.length > 0 ? newSessions[newSessions.length - 1].sessionId : null;

        return { sessions: newSessions, activeSessionId: newActiveId, accessToken: null }
      }),

      logoutAll: () => set(() => {
        return { sessions: [], activeSessionId: null, accessToken: null }
      }),
    }), {
    name: 'user-session',
    version: 1,
    migrate: (persistedState, version) => {
      if (version === 0) {
        // default values
        return {
          isReady: false,
          sessions: [],
          activeSessionId: null,
          accessToken: null,
        }
      }
      return persistedState
    },
    partialize: (state) =>
      Object.fromEntries(
        Object.entries(state).filter(([key]) => key !== 'isReady')
      ),
  })
)

export const useUserSessionStore = (selector) => useStore(store, selector);

export const getActiveSessionId = () => store.getState().activeSessionId;

// Updated to grab token from the store for the active session
export const getAccessToken = () => {
  const state = store.getState();
  const token = state.accessToken;
  return token && isValidToken(token) ? token : null;
}
export const setAccessToken = (accessToken) => {
  const state = store.getState();
  state.setAccessToken(accessToken && isValidToken(accessToken) ? accessToken : null);
}
export const getSessions = () => store.getState().sessions

export const login = (sessionId, user, accessToken) => store.getState().login(sessionId, user, accessToken)
export const updateUser = (sessionId, user) => store.getState().updateUser(sessionId, user)
export const useIsLogged = () => {
  const { activeSessionId } = store.getState();
  const { sessions } = store.getState();
  return !!activeSessionId && sessions.some(s => s.sessionId === activeSessionId);
}

export const logout = () => store.getState().logout()
export const logoutAll = () => store.getState().logoutAll()
export const switchSession = (sessionId) => store.getState().switchSession(sessionId)

export default useUserSessionStore;
